import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMidiMappingStore } from '@/stores/midiMappingStore'

interface RingPosition {
  id: string
  label: string
  rect: DOMRect
  isTarget: boolean
}

/**
 * Full-screen visual overlay rendered when MIDI Learn mode is active.
 * Draws a soft ring around every mappable control that's currently
 * mounted, plus a pulse on whichever control is armed for capture
 * (`learnTargetId`). Non-interactive: actual "click to arm" is handled
 * inside each component's own `onClick` via `armForLearn` from
 * `useMidiMappable`, which keeps the pointer path simple and avoids
 * surprises with transformed or overflowing parents.
 *
 * Refreshes ring positions every animation frame while active, which
 * keeps them glued to their elements during scrolling, resizing, and
 * parent transforms. For the expected control counts (~100 max),
 * this cost is negligible.
 */
export const MidiLearnOverlay: React.FC = () => {
  const learnMode = useMidiMappingStore(s => s.learnMode)
  const descriptors = useMidiMappingStore(s => s.descriptors)
  const learnTargetId = useMidiMappingStore(s => s.learnTargetId)
  const [rings, setRings] = useState<RingPosition[]>([])
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!learnMode) {
      setRings([])
      return
    }
    const tick = () => {
      const next: RingPosition[] = []
      for (const descriptor of Object.values(descriptors)) {
        const el = descriptor.getElement?.()
        if (!el) continue
        const rect = el.getBoundingClientRect()
        // Skip 0×0 rects (unmounted-but-not-yet-cleaned, display:none,
        // or positioned off-screen via virtualization).
        if (rect.width === 0 || rect.height === 0) continue
        next.push({
          id: descriptor.id,
          label: descriptor.label,
          rect,
          isTarget: descriptor.id === learnTargetId,
        })
      }
      setRings(next)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [learnMode, descriptors, learnTargetId])

  if (!learnMode || typeof document === 'undefined') return null

  return createPortal(
    <div
      aria-hidden
      className="fixed inset-0 pointer-events-none z-[9998]"
      style={{ contain: 'layout paint' }}
    >
      {rings.map(ring => (
        <div
          key={ring.id}
          className={`absolute rounded-md transition-[box-shadow] duration-75 ${
            ring.isTarget
              ? 'animate-pulse shadow-[0_0_0_3px_var(--accent),0_0_20px_4px_var(--accent-halo)]'
              : 'shadow-[0_0_0_2px_var(--accent-halo)]'
          }`}
          style={{
            left: ring.rect.left - 4,
            top: ring.rect.top - 4,
            width: ring.rect.width + 8,
            height: ring.rect.height + 8,
          }}
        />
      ))}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-md bg-accent text-[color:var(--accent-ink)] text-[11px] font-mono uppercase tracking-[0.14em] pointer-events-none shadow-lg">
        MIDI Learn — click a control, then move a knob or press a key
      </div>
    </div>,
    document.body,
  )
}
